import React from "react";
import { useTranslation } from "react-i18next";
import { Card, CardBody, Col, Row, Spinner } from "reactstrap";
import moment from "moment";

import { LogServiceBreakdownDto } from "../../interfaces";

type LogServiceHealthProps = {
  services?: LogServiceBreakdownDto[];
  isLoading?: boolean;
  activeService?: string;
  onSelectService: (serviceName: string) => void;
  onSelectServiceLevel: (serviceName: string, level: string) => void;
};

type Health = { key: string; label: string; color: string };

function resolveHealth(service: LogServiceBreakdownDto): Health {
  const errorRate = service.total ? service.errors / service.total : 0;
  if (service.errors > 0 && errorRate >= 0.25) {
    return { key: "critical", label: "critical", color: "danger" };
  }
  if (service.errors > 0) {
    return { key: "degraded", label: "degraded", color: "warning" };
  }
  if (service.warnings > 0) {
    return { key: "watch", label: "watch", color: "warning" };
  }
  return { key: "healthy", label: "healthy", color: "success" };
}

export const LogServiceHealth: React.FC<LogServiceHealthProps> = ({
  services,
  isLoading,
  activeService,
  onSelectService,
  onSelectServiceLevel,
}) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <Card className="logs-service-health">
        <CardBody className="text-center py-5">
          <Spinner />
        </CardBody>
      </Card>
    );
  }

  if (!services?.length) {
    return (
      <Card className="logs-service-health">
        <CardBody className="text-center text-muted py-5">
          {t("noRecordsFound")}
        </CardBody>
      </Card>
    );
  }

  const levelCounts = (service: LogServiceBreakdownDto) => [
    { key: "error", value: service.errors, className: "error" },
    { key: "warn", value: service.warnings, className: "warn" },
    { key: "info", value: service.info, className: "info" },
    { key: "debug", value: service.debug, className: "debug" },
  ];

  return (
    <div className="logs-service-health">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h6 className="mb-0">{t("serviceHealth")}</h6>
        <span className="text-muted small">{t("clickToFilter")}</span>
      </div>
      <Row className="g-3">
        {services.map((service) => {
          const health = resolveHealth(service);
          const isActive = activeService === service.serviceName;
          return (
            <Col md={6} xl={4} key={service.serviceName}>
              <Card
                className={`logs-service-card logs-service-card--${health.color} ${
                  isActive ? "logs-service-card--active" : ""
                }`}
              >
                <CardBody>
                  <div className="logs-service-card__head">
                    <button
                      type="button"
                      className="logs-service-card__name"
                      onClick={() => onSelectService(service.serviceName)}
                      title={service.serviceName}
                    >
                      <span
                        className={`logs-service-card__dot bg-${health.color}`}
                      />
                      {service.serviceName}
                    </button>
                    <span
                      className={`badge bg-${health.color}-subtle text-${health.color} text-uppercase`}
                    >
                      {t(health.label)}
                    </span>
                  </div>

                  <div className="logs-service-card__meta text-muted small mb-3">
                    <span>
                      {t("total")}: <strong>{service.total}</strong>
                    </span>
                    {service.httpErrors > 0 && (
                      <span className="text-danger">
                        {t("httpErrors")}: <strong>{service.httpErrors}</strong>
                      </span>
                    )}
                    {service.lastActivity && (
                      <span title={moment(service.lastActivity).format("LLL")}>
                        {moment(service.lastActivity).fromNow()}
                      </span>
                    )}
                  </div>

                  <div className="logs-service-card__levels">
                    {levelCounts(service).map((level) => (
                      <button
                        type="button"
                        key={level.key}
                        className={`logs-level-stat logs-level-stat--${level.className}`}
                        onClick={() =>
                          onSelectServiceLevel(service.serviceName, level.key)
                        }
                        disabled={!level.value}
                      >
                        <span className="logs-level-stat__value">
                          {level.value}
                        </span>
                        <span className="logs-level-stat__label">
                          {t(level.key)}
                        </span>
                      </button>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </Col>
          );
        })}
      </Row>
    </div>
  );
};
